import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { ActionPermissions, Tag } from '../../../@types';
import { ServiceObject } from '../../../@types/service.type';
import {
  AutocompleteTagType,
  getNewTagsMapped,
  getSelectedExistingTagsMapped,
} from '../../../components/organisms/Tags';
import { EntityType } from '../../../configs/enums';
import { isUserHasPermission, PermissionInput } from '../../../configs/permissions';
import { GetServiceKey } from '../../../configs/services';
import { useAddTags, useGetAllTags } from '../../../queries/tags-query';

interface ServiceTags {
  allTags: Tag[];
  /** Undefined when the user cannot edit tags, so no edit affordance renders. */
  onSaveTags?: (args: {
    selectedTags: (AutocompleteTagType | string)[];
    newTags: Tag[];
  }) => Promise<void>;
}

/**
 * Tag list plus the save handler for the job detail header. The whole selected
 * set is posted every time — POST /tags reconciles the allocations, so removing
 * a tag means leaving it out.
 */
export const useServiceTags = (
  serviceId: number,
  permissions: PermissionInput[] | null | undefined,
  onSaved: () => void
): ServiceTags => {
  const canEditTags = isUserHasPermission(
    ActionPermissions.Service_Edit_Service_Parameters_General,
    permissions
  );
  const allTagsQuery = useGetAllTags(EntityType.TYPE_SERVICES, { enabled: canEditTags });
  const addTagsMutation = useAddTags();
  const queryClient = useQueryClient();

  const saveTags = useCallback(
    async ({
      selectedTags,
      newTags,
    }: {
      selectedTags: (AutocompleteTagType | string)[];
      newTags: Tag[];
    }) => {
      const existingTags = getSelectedExistingTagsMapped(selectedTags);
      const serviceKey = [GetServiceKey, serviceId];
      const previous = queryClient.getQueryData<ServiceObject>(serviceKey);

      // Paint the new set straight onto the cached job so the header chips
      // update on click rather than after the round trip plus refetch. Tags
      // created in this save have no id yet; the refetch below replaces them
      // with the saved rows.
      queryClient.setQueryData<ServiceObject>(serviceKey, (current) =>
        current ? { ...current, tags: [...existingTags, ...newTags] } : current
      );

      try {
        await addTagsMutation.mutateAsync({
          entityTypeId: EntityType.TYPE_SERVICES,
          entityId: serviceId,
          freshTags: getNewTagsMapped(newTags),
          existingTags,
        });
      } catch (error) {
        queryClient.setQueryData(serviceKey, previous);
        throw error;
      }
      onSaved();
    },
    [addTagsMutation, serviceId, onSaved, queryClient]
  );

  return {
    allTags: allTagsQuery.data ?? [],
    onSaveTags: canEditTags ? saveTags : undefined,
  };
};
